import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  ArchiveIcon,
  ChevronIcon,
  CloseIcon,
  EditIcon,
  FilterIcon,
  KebabIcon,
  PinFilledIcon,
  PinIcon,
  PlusIcon,
  SearchIcon,
  SortIcon,
} from "./reportIcons";
import { ReportDetailsDrawer } from "../../components/reports/ReportDetailsDrawer";
import { ReportLogos } from "./reportLogos";
import { KipReportBuilderSidebar } from "../../components/reports/KipReportBuilderSidebar";
import { getCustomReports } from "../../slices/customReports";
import { ReportsBulkAccessModal } from "../../components/reports/ReportsBulkAccessModal";
import { availableReports } from "../../consts/reports";
import { enumPermissions, isAdminRole } from "../../consts/management/users/users";
import {
  archiveReport,
  archiveReports,
  getAvailableReports,
  getPinnedReports,
  recordReportLastViewed,
  ReportsLoadingKeys,
  setPinnedReports,
  togglePinnedReport,
  unarchiveReport,
  unarchiveReports,
} from "../../slices/reportsSlice";
import {
  compareReportsByColumn,
  formatRelativeDate,
  formatReportDate,
  getDefaultSortDirection,
  getReportCategory,
  REPORT_CATEGORIES,
  REPORT_SORT_COLUMNS,
} from "./reportDashboardHelpers";
import { arrayHasItems } from "../../helpers/common";
import { LoaderSpinner } from "../../components/LoaderSpinner";

function SortableColumnHeader({ label, column, sortColumn, sortDirection, onSort }) {
  const isActive = sortColumn === column;
  return (
    <button
      type="button"
      onClick={() => onSort(column)}
      className={`inline-flex items-center gap-1 text-left hover:text-[#0f1115] ${
        isActive ? "text-[#0f1115]" : ""
      }`}
    >
      <span>{label}</span>
      <SortIcon className={`h-[12px] w-[12px] shrink-0 ${isActive ? "" : "opacity-40"}`} />
    </button>
  );
}

function OwnerBadge() {
  return (
    <div className="flex min-w-0 items-center gap-[7px]">
      <div className="flex h-5 w-5 flex-none items-center justify-center rounded-full bg-[#e6e6ea] text-[9px] font-semibold text-[#4b5060]">
        E
      </div>
      <span className="truncate text-[12.5px] text-[#4b5060]">Eadin</span>
    </div>
  );
}

const ReportDashboard = ({ 
  access,
  marginFromSidebar
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const {
    locationsOfReports,
    pinnedReportIds,
    archivedReports,
    loadingKeys,
  } = useSelector((state) => state.reports);
  const { currentUser } = useSelector((state) => state.auth);
  const { reports: customReportsList } = useSelector((state) => state.customReports);

  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All categories");
  const [selectedIds, setSelectedIds] = useState([]);
  const [drawerReportId, setDrawerReportId] = useState(null);
  const [reportNamesMap, setReportNamesMap] = useState({});
  const [bulkReportIds, setBulkReportIds] = useState(null);
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [isBuilderOpen, setIsBuilderOpen] = useState(false);
  const [customReportToEdit, setCustomReportToEdit] = useState(null);
  const [reportView, setReportView] = useState("active");
  const [openMenuId, setOpenMenuId] = useState(null);
  const [categoryMenuOpen, setCategoryMenuOpen] = useState(false);

  useEffect(() => {
    if (openMenuId === null && !categoryMenuOpen) return undefined;
    const closeAll = () => { setOpenMenuId(null); setCategoryMenuOpen(false); };
    const onMouseDown = (e) => {
      if (!e.target.closest?.("[data-report-menu]")) closeAll();
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") closeAll();
    };
    document.addEventListener("mousedown", onMouseDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onMouseDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openMenuId, categoryMenuOpen]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        const res = await fetch("/api/v1/metadata/reports-names", {
          headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        });
        if (!res.ok) return;
        const json = await res.json().catch(() => ({}));
        const list = Array.isArray(json?.reports) ? json.reports : [];
        const map = list.reduce((acc, item) => {
          if (item?.id && item?.name) acc[item.id] = item.name;
          return acc;
        }, {});
        if (mounted) setReportNamesMap(map);
      } catch (e) {
        console.error(e);
      }
    };
    load();
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    if (currentUser?.id) {
      dispatch(getPinnedReports({}));
      dispatch(getAvailableReports());
      dispatch(getCustomReports());
    }
  }, [currentUser?.id, dispatch]);

  useEffect(() => {
    const onUpdated = (e) => {
      const id = e?.detail?.id;
      const name = e?.detail?.name;
      if (!id || !name) return;
      setReportNamesMap((prev) => ({ ...(prev || {}), [id]: name }));
    };
    window.addEventListener("reports-names-updated", onUpdated);
    return () => window.removeEventListener("reports-names-updated", onUpdated);
  }, []);

  const locationByReport = useMemo(() => {
    return (Array.isArray(locationsOfReports) ? locationsOfReports : []).reduce((acc, item) => {
      if (item?.report_name) acc[item.report_name] = item;
      return acc;
    }, {});
  }, [locationsOfReports]);

  const reportLocations = useMemo(() => {
    return Object.entries(locationByReport).reduce((acc, [reportName, item]) => {
      acc[reportName] = String(item?.tab_name || "")
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      return acc;
    }, {});
  }, [locationByReport]);

  const userPermissions = currentUser?.permissions || [];
  const allowedParentIds = new Set();
  (userPermissions || []).forEach((permissionGroup) => {
    const [parentKey, children] = Object.entries(permissionGroup || {})[0] || [];
    (children || []).forEach((childPermission) => {
      const [, childAccess] = Object.entries(childPermission || {})[0] || [];
      if (childAccess === "edit" || childAccess === "view") {
        allowedParentIds.add(parentKey);
      }
    });
  });

  const pinnedIdSet = useMemo(
    () => new Set(arrayHasItems(pinnedReportIds) ? pinnedReportIds : []),
    [pinnedReportIds],
  );
  const isAdmin = isAdminRole(currentUser?.role);
  const showArchivedReports = isAdmin && reportView === "archived";
  const archiveById = useMemo(() => ({
    ...(archivedReports || {}),
    ...(Array.isArray(customReportsList) ? customReportsList.reduce((acc, report) => {
      if (report?.archived && report?.reportId) acc[report.reportId] = report;
      return acc;
    }, {}) : {}),
  }), [archivedReports, customReportsList]);

  const displayedReports = useMemo(() => {
    const base = access === "edit"
      ? availableReports
      : availableReports.filter((report) => {
          const tabNames = reportLocations[report.id] || [];
          return tabNames.length > 0 && tabNames.some((name) => allowedParentIds.has(name));
        });

    const customEntries = (Array.isArray(customReportsList) ? customReportsList : [])
      .filter((report) => {
        if (!isAdmin) return !archiveById[report.id];
        return Boolean(archiveById[report.id]) === showArchivedReports;
      })
      .map((report) => ({
        id: report.id,
        name: report.name || "",
        description: report.description || "Custom report built with Kip",
        path: `/report/custom/${report.id}`,
        isCustom: true,
        category: getReportCategory(report),
        archived: Boolean(archiveById[report.id]),
        ...(archiveById[report.id] || {}),
        tabKeys: reportLocations[report.id] || [],
        pinned: pinnedIdSet.has(report.id),
        lastViewed:
          locationByReport[report.id]?.lastViewed
          || locationByReport[report.id]?.last_viewed
          || null,
      }));

    return [
      ...base
        .filter((report) => {
          if (!isAdmin) return !archiveById[report.id];
          return Boolean(archiveById[report.id]) === showArchivedReports;
        })
        .map((report) => ({
          ...report,
          name: reportNamesMap?.[report.id] || report.name || "",
          category: getReportCategory(report),
          archived: Boolean(archiveById[report.id]),
          ...(archiveById[report.id] || {}),
          tabKeys: reportLocations[report.id] || [],
          pinned: pinnedIdSet.has(report.id),
          lastViewed:
            locationByReport[report.id]?.lastViewed
            || locationByReport[report.id]?.last_viewed
            || null,
        })),
      ...customEntries,
    ];
  }, [access, allowedParentIds, archiveById, customReportsList, isAdmin, locationByReport, pinnedIdSet, reportLocations, reportNamesMap, showArchivedReports]);

  const visibleReports = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();
    return [...displayedReports]
      .filter((report) => {
        const accessText = (report.tabKeys || [])
          .map((key) => enumPermissions[key] || key)
          .join(" ")
          .toLowerCase();
        const matchesSearch =
          normalizedQuery.length === 0 ||
          report.name.toLowerCase().includes(normalizedQuery) ||
          report.description.toLowerCase().includes(normalizedQuery) ||
          "eadin".includes(normalizedQuery) ||
          accessText.includes(normalizedQuery);
        const matchesCategory =
          categoryFilter === "All categories" || report.category === categoryFilter;
        return matchesSearch && matchesCategory;
      })
      .sort((left, right) => {
        const pinnedDelta = Number(right.pinned) - Number(left.pinned);
        if (pinnedDelta !== 0) return pinnedDelta;
        if (sortColumn) {
          return compareReportsByColumn(left, right, sortColumn, sortDirection);
        }
        const leftTime = left.lastViewed ? new Date(left.lastViewed).getTime() : 0;
        const rightTime = right.lastViewed ? new Date(right.lastViewed).getTime() : 0;
        return rightTime - leftTime;
      });
  }, [categoryFilter, displayedReports, searchQuery, sortColumn, sortDirection]);

  const visibleIds = visibleReports.map((report) => report.id);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const canEdit = access === "edit" || isAdmin;

  const handleToggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds((current) => current.filter((id) => !visibleIds.includes(id)));
      return;
    }
    setSelectedIds((current) => Array.from(new Set([...current, ...visibleIds])));
  };

  const toggleSelected = (reportId) => {
    setSelectedIds((current) =>
      current.includes(reportId)
        ? current.filter((id) => id !== reportId)
        : [...current, reportId],
    );
  };

  const handleTogglePinned = async (report) => {
    try {
      await dispatch(togglePinnedReport({ reportId: report.id })).unwrap();
    } catch (e) {
      console.error(e);
    }
  };

  const handleToggleArchived = async (report) => {
    if (!report?.id || !isAdmin) return;
    try {
      if (report.archived) {
        await dispatch(unarchiveReport({ reportId: report.id })).unwrap();
      } else {
        // eslint-disable-next-line no-alert
        if (!window.confirm(`Archive "${report.name}"? All non-admin users will lose access.`)) return;
        await dispatch(archiveReport({ reportId: report.id })).unwrap();
      }
      await Promise.all([
        dispatch(getCustomReports()).unwrap(),
        dispatch(getAvailableReports()).unwrap(),
      ]);
    } catch (error) {
      console.error(error);
    }
  };

  const selectedReports = visibleReports.filter((report) => selectedIds.includes(report.id));
  const selectedReportsArePinned = selectedReports.length > 0
    && selectedReports.every((report) => report.pinned);

  const refetchReportLists = async () => {
    await Promise.all([
      dispatch(getCustomReports()).unwrap(),
      dispatch(getAvailableReports()).unwrap(),
    ]);
  };

  const handleBulkArchive = async () => {
    if (!isAdmin || !selectedIds.length) return;
    const actionLabel = reportView === "archived" ? "unarchive" : "archive";
    const confirmation = reportView === "archived"
      ? `Unarchive ${selectedIds.length} selected report${selectedIds.length === 1 ? "" : "s"}?`
      : `Archive ${selectedIds.length} selected report${selectedIds.length === 1 ? "" : "s"}? All non-admin users will lose access.`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(confirmation)) return;
    try {
      const thunk = reportView === "archived" ? unarchiveReports : archiveReports;
      await dispatch(thunk({ reportIds: selectedIds })).unwrap();
      await refetchReportLists();
      setSelectedIds([]);
    } catch (error) {
      console.error(`Failed to ${actionLabel} selected reports.`, error);
    }
  };

  const handleBulkSetPinned = async () => {
    if (!selectedIds.length) return;
    try {
      await dispatch(setPinnedReports({
        reportIds: selectedIds,
        pinned: !selectedReportsArePinned,
      })).unwrap();
      setSelectedIds([]);
    } catch (error) {
      console.error("Failed to update selected report pins.", error);
    }
  };

  const openEditor = (reportId) => {
    const customReport = customReportsList?.find((report) => report.id === reportId);
    if (customReport) {
      setCustomReportToEdit(customReport);
      setIsBuilderOpen(true);
      return;
    }
    setDrawerReportId(reportId);
  };

  const handleSortColumn = (column) => {
    if (sortColumn === column) {
      setSortDirection((current) => (current === "asc" ? "desc" : "asc"));
      return;
    }
    setSortColumn(column);
    setSortDirection(getDefaultSortDirection(column));
  };

  const handleViewReport = async (report) => {
    try {
      await dispatch(
        recordReportLastViewed({ reportName: report.id })
      ).unwrap();
    } catch (e) {
      console.error(e);
    }
    navigate(report.path);
  };

  const resetFilters = () => {
    setSearchQuery("");
    setCategoryFilter("All categories");
  };

  const categoryCounts = useMemo(
    () => displayedReports.reduce((acc, r) => { acc[r.category] = (acc[r.category] || 0) + 1; return acc; }, {}),
    [displayedReports],
  );

  const handleCategoryFilterChange = (value) => {
    setCategoryFilter(value);
    setSelectedIds([]);
    setBulkReportIds(null);
  };

  const pinnedReportsIsLoading = loadingKeys.includes(ReportsLoadingKeys.getPinnedReports);

  const bulkBar = canEdit && selectedIds.length > 0 && typeof document !== "undefined"
    ? createPortal(
      <div className="fixed bottom-[18px] left-1/2 z-[100] flex -translate-x-1/2 items-center gap-2.5 rounded-[10px] bg-[#0f1115] py-2 pl-3.5 pr-2 text-[12.5px] text-white shadow-[0_12px_32px_rgba(15,17,21,0.18)]">
        <span className="font-medium">{selectedIds.length} selected</span>
        <span className="mx-1 h-4 w-px bg-white/20" />
        {[
          { label: "Visibility", onClick: () => setBulkReportIds([...selectedIds]) },
          { label: selectedReportsArePinned ? "Unpin" : "Pin", onClick: handleBulkSetPinned },
          ...(isAdmin ? [{ label: reportView === "archived" ? "Restore" : "Archive", onClick: handleBulkArchive }] : []),
        ].map((a) => (
          <button key={a.label} type="button" onClick={a.onClick} className="h-7 rounded-md px-2.5 text-[12.5px] text-white hover:bg-white/10">
            {a.label}
          </button>
        ))}
        <button type="button" aria-label="Clear selection" onClick={() => setSelectedIds([])} className="inline-flex h-7 w-7 items-center justify-center rounded-md text-white/70 hover:bg-white/10 hover:text-white">
          <CloseIcon className="h-[13px] w-[13px]" />
        </button>
      </div>,
      document.body,
    )
    : null;

  const drawerReport = drawerReportId
    ? displayedReports.find((report) => report.id === drawerReportId) ?? null
    : null;

  const grid = canEdit
    ? "grid-cols-[28px_minmax(0,1fr)_120px_110px_120px_84px]"
    : "grid-cols-[minmax(0,1fr)_120px_110px_120px_84px]";
  const cellText = "text-[12.5px] text-[#4b5060]";
  const rowMenuBtn = "inline-flex h-[30px] w-full items-center gap-2 rounded-md px-2.5 text-[12.5px] text-[#0f1115] hover:bg-[#f3f3f5]";

  return (
    <div className="min-h-screen bg-[#f7f7f8] text-[#0f1115] antialiased">
      <div className="flex min-h-screen">
      <div className="flex min-h-screen min-w-0 flex-1 flex-col">
        <header className="px-4 pt-[14px] sm:px-7">
          <div className="text-[10.5px] font-semibold uppercase tracking-[.14em] text-[#7b8090]">Eadin Internal Tool Suite</div>
          <div className="mt-1.5 flex items-end justify-between gap-4">
            <h1 className="text-[20px] font-[650] tracking-[-.02em]">Reports</h1>
            {canEdit && (
              <button
                type="button"
                onClick={() => setIsBuilderOpen(true)}
                className="inline-flex h-[34px] items-center gap-[7px] rounded-[7px] border border-[#0f1115] bg-[#0f1115] px-[13px] text-[13px] font-medium text-white hover:bg-[#1c1f26]"
              >
                <PlusIcon className="h-[14px] w-[14px]" />
                Create report
              </button>
            )}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2.5">
            <label className="flex h-9 min-w-[200px] flex-1 items-center gap-2.5 rounded-[10px] border border-[#ececef] bg-white px-3.5 shadow-[0_1px_2px_rgba(15,17,21,0.04)] focus-within:border-[#c9cbd3]">
              <SearchIcon className="h-[15px] w-[15px] flex-none text-[#7b8090]" />
              <input
                type="search"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedIds([]);
                  setBulkReportIds(null);
                }}
                placeholder="Search reports, owners, access groups, or categories…"
                className="min-w-0 flex-1 border-0 bg-transparent text-[13.5px] text-[#0f1115] outline-none placeholder:text-[#a2a6b3]"
              />
              <kbd className="hidden rounded border border-[#ececef] bg-[#f7f7f8] px-1.5 py-px font-sans text-[10.5px] text-[#a2a6b3] sm:block">/</kbd>
            </label>
            <div className="relative" data-report-menu>
              <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={categoryMenuOpen}
                onClick={() => setCategoryMenuOpen((o) => !o)}
                className="inline-flex h-[34px] items-center gap-[7px] rounded-[7px] border border-[#ececef] bg-white px-[13px] text-[13px] font-medium text-[#0f1115] hover:bg-[#fafafb]"
              >
                <FilterIcon className="h-[14px] w-[14px] text-[#7b8090]" />
                {categoryFilter}
                <ChevronIcon className="h-[14px] w-[14px] text-[#7b8090]" />
              </button>
              {categoryMenuOpen && (
                <div role="listbox" className="absolute left-0 top-[calc(100%+4px)] z-30 min-w-[200px] rounded-lg border border-[#ececef] bg-white p-1 shadow-[0_8px_24px_rgba(15,17,21,0.08)]">
                  {REPORT_CATEGORIES.map((cat) => {
                    const count = cat === "All categories" ? displayedReports.length : categoryCounts[cat] || 0;
                    const on = categoryFilter === cat;
                    return (
                      <button
                        key={cat}
                        type="button"
                        role="option"
                        aria-selected={on}
                        onClick={() => { handleCategoryFilterChange(cat); setCategoryMenuOpen(false); }}
                        className={`flex h-[30px] w-full items-center justify-between gap-3 rounded-md px-2.5 text-[12.5px] hover:bg-[#f3f3f5] ${on ? "bg-[#f3f3f5] font-medium" : ""}`}
                      >
                        <span>{cat}</span>
                        <span className="text-[11px] text-[#a2a6b3]">{count}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            {isAdmin && (
              <div className="inline-flex h-[34px] items-center rounded-[7px] border border-[#ececef] bg-white p-0.5">
                {[
                  { value: "active", label: "Active" },
                  { value: "archived", label: "Archived" },
                ].map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => { setReportView(option.value); setSelectedIds([]); setBulkReportIds(null); setOpenMenuId(null); setDrawerReportId(null); }}
                    className={`h-full rounded-[5px] px-[11px] text-[13px] font-medium ${
                      reportView === option.value ? "bg-[#e6e6ea] text-[#0f1115]" : "text-[#7b8090] hover:text-[#0f1115]"
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        </header>

        <div className="px-4 pb-10 pt-4 sm:px-7">
          <div className="mb-2 flex items-center justify-between text-[11px] font-semibold uppercase tracking-[.1em] text-[#7b8090]">
            <span>{reportView === "archived" ? "Archived reports" : "All reports"}</span>
            <span className="font-medium normal-case tracking-normal text-[#a2a6b3]">
              {visibleReports.length} of {displayedReports.length}
            </span>
          </div>

          <div className="rounded-[10px] border border-[#ececef] bg-white">
            {pinnedReportsIsLoading ? (
              <div className="flex min-h-[240px] items-center justify-center"><LoaderSpinner /></div>
            ) : visibleReports.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
                <p className="text-[13px] font-semibold">No reports match</p>
                <p className="text-[12.5px] text-[#7b8090]">Try a different search or clear the filters.</p>
                <button type="button" onClick={resetFilters} className="mt-2 inline-flex h-[30px] items-center rounded-[7px] border border-[#ececef] bg-white px-3 text-[12.5px] font-medium hover:bg-[#fafafb]">Reset filters</button>
              </div>
            ) : (
              <>
                <div className={`hidden h-9 items-center gap-3 rounded-t-[10px] border-b border-[#ececef] bg-[#fafafb] px-3.5 text-[11.5px] font-semibold text-[#7b8090] md:grid ${grid}`}>
                  {canEdit && (
                    <input
                      type="checkbox"
                      aria-label="Select all visible reports"
                      checked={allVisibleSelected}
                      onChange={handleToggleSelectAll}
                      className="h-[15px] w-[15px] cursor-pointer accent-[#0f1115]"
                    />
                  )}
                  <SortableColumnHeader label="Name" column={REPORT_SORT_COLUMNS.name} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSortColumn} />
                  <SortableColumnHeader label="Category" column={REPORT_SORT_COLUMNS.category} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSortColumn} />
                  <SortableColumnHeader label="Last viewed" column={REPORT_SORT_COLUMNS.lastViewed} sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSortColumn} />
                  <span>Created by</span>
                  <span />
                </div>

                <div>
                  {visibleReports.map((report) => {
                    const isSelected = selectedIds.includes(report.id);
                    const menuOpen = openMenuId === report.id;
                    const rowMenu = (
                      <div className="relative" data-report-menu>
                        <button
                          type="button"
                          aria-label={`Actions for ${report.name}`}
                          aria-expanded={menuOpen}
                          onClick={(e) => { e.stopPropagation(); setOpenMenuId(menuOpen ? null : report.id); }}
                          className={`inline-flex h-[26px] w-[26px] items-center justify-center rounded-md text-[#7b8090] hover:bg-[#f3f3f5] hover:text-[#0f1115] ${
                            menuOpen ? "bg-[#f3f3f5] text-[#0f1115]" : "md:opacity-0 md:group-hover:opacity-100 md:focus:opacity-100"
                          }`}
                        >
                          <KebabIcon className="h-[13px] w-[13px]" />
                        </button>
                        {menuOpen && (
                          <div className="absolute right-0 top-[calc(100%+4px)] z-20 min-w-[140px] rounded-lg border border-[#ececef] bg-white p-1 shadow-[0_8px_24px_rgba(15,17,21,0.08)]">
                            <button type="button" onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); handleTogglePinned(report); }} className={rowMenuBtn}>
                              {report.pinned ? <PinFilledIcon className="h-[13px] w-[13px] text-[#7b8090]" /> : <PinIcon className="h-[13px] w-[13px] text-[#7b8090]" />}
                              {report.pinned ? "Unpin" : "Pin"}
                            </button>
                            {isAdmin && (
                              <button type="button" onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); handleToggleArchived(report); }} className={rowMenuBtn}>
                                <ArchiveIcon className="h-[13px] w-[13px] text-[#7b8090]" />
                                {report.archived ? "Restore" : "Archive"}
                              </button>
                            )}
                            {canEdit && (
                              <button type="button" onClick={(e) => { e.stopPropagation(); setOpenMenuId(null); openEditor(report.id); }} className={rowMenuBtn}>
                                <EditIcon className="h-[13px] w-[13px] text-[#7b8090]" />
                                Edit
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    );
                    const nameCell = (
                      <div className="flex min-w-0 items-start gap-2.5">
                        <span className="mt-[5px] grid w-3 flex-none place-items-center text-[#0f1115]">
                          {report.pinned && <PinFilledIcon className="h-[11px] w-[11px]" />}
                        </span>
                        <ReportLogos reportId={report.id} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="truncate text-[13px] font-semibold tracking-[-.005em] text-[#0f1115]">{report.name}</span>
                            {report.archived && (
                              <span className="inline-flex h-[18px] flex-none items-center rounded bg-[#f3f3f5] px-1.5 text-[10.5px] font-medium text-[#7b8090]">Archived</span>
                            )}
                          </div>
                          <div className="mt-px truncate text-[12px] text-[#7b8090]">{report.description}</div>
                        </div>
                      </div>
                    );
                    const onRowKey = (e) => {
                      if (e.key === "Enter" || e.key === " ") { e.preventDefault(); handleViewReport(report); }
                    };
                    return (
                      <React.Fragment key={report.id}>
                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => handleViewReport(report)}
                          onKeyDown={onRowKey}
                          className={`group hidden min-h-[52px] cursor-pointer items-center gap-3 border-b border-[#f3f3f5] px-3.5 py-2 last:border-b-0 md:grid ${grid} ${
                            isSelected ? "bg-[#f3f3f5]" : "hover:bg-[#fafafb]"
                          }`}
                        >
                          {canEdit && (
                            <input
                              type="checkbox"
                              aria-label={`Select ${report.name}`}
                              checked={isSelected}
                              onChange={() => toggleSelected(report.id)}
                              onClick={(e) => e.stopPropagation()}
                              className={`h-[15px] w-[15px] cursor-pointer accent-[#0f1115] ${isSelected ? "" : "opacity-0 group-hover:opacity-100 focus:opacity-100"}`}
                            />
                          )}
                          {nameCell}
                          <div>
                            <span className="inline-flex h-[22px] items-center rounded-md bg-[#f3f3f5] px-2 text-[12px] font-medium text-[#4b5060]">{report.category}</span>
                          </div>
                          <div className={cellText}>
                            {formatRelativeDate(report.lastViewed)}
                            <small className="block text-[11px] text-[#a2a6b3]">{formatReportDate(report.lastViewed)}</small>
                          </div>
                          <OwnerBadge />
                          <div className="flex items-center justify-end" onClick={(e) => e.stopPropagation()}>{rowMenu}</div>
                        </div>

                        <div
                          role="button"
                          tabIndex={0}
                          onClick={() => handleViewReport(report)}
                          onKeyDown={onRowKey}
                          className={`cursor-pointer border-b border-[#f3f3f5] px-3.5 py-2.5 last:border-b-0 md:hidden ${isSelected ? "bg-[#f3f3f5]" : ""}`}
                        >
                          <div className="flex items-start justify-between gap-2.5">
                            <div className="flex min-w-0 items-start gap-2.5">
                              {canEdit && (
                                <input
                                  type="checkbox"
                                  aria-label={`Select ${report.name}`}
                                  checked={isSelected}
                                  onChange={() => toggleSelected(report.id)}
                                  onClick={(e) => e.stopPropagation()}
                                  className="mt-0.5 h-[15px] w-[15px] cursor-pointer accent-[#0f1115]"
                                />
                              )}
                              {nameCell}
                            </div>
                            <div onClick={(e) => e.stopPropagation()}>{rowMenu}</div>
                          </div>
                          <div className="mt-1.5 flex flex-wrap items-center gap-2 pl-[22px] text-[12px] text-[#7b8090]">
                            <span className="inline-flex h-[22px] items-center rounded-md bg-[#f3f3f5] px-2 font-medium text-[#4b5060]">{report.category}</span>
                            <span>{formatReportDate(report.lastViewed)}</span>
                          </div>
                        </div>
                      </React.Fragment>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <ReportDetailsDrawer
        isOpen={Boolean(drawerReportId)}
        report={drawerReport}
        onClose={() => setDrawerReportId(null)}
        canEdit={canEdit}
        isAdmin={isAdmin}
        onOpenReport={handleViewReport}
        onTogglePinned={handleTogglePinned}
        onToggleArchived={handleToggleArchived}
      />
      </div>
      {bulkBar}
      <ReportsBulkAccessModal
        isOpen={canEdit && Boolean(bulkReportIds?.length)}
        reportIds={bulkReportIds || []}
        cancelClick={() => { setBulkReportIds(null); }}
        marginFromSidebar={marginFromSidebar}
      />
      <KipReportBuilderSidebar
        isOpen={isBuilderOpen}
        onClose={() => {
          setIsBuilderOpen(false);
          setCustomReportToEdit(null);
        }}
        marginFromSidebar={marginFromSidebar}
        initialProposal={customReportToEdit}
        onReportCreated={(report) => {
          setIsBuilderOpen(false);
          setCustomReportToEdit(null);
          if (report?.id) navigate(`/report/custom/${report.id}`);
        }}
      />
    </div>
  );
};

export default ReportDashboard;
